import {useState, useEffect} from "react";
import {CHARACTER_INFO, CharacterInfo} from "../assets/constants/characterInfo";
import {getBackgroundVideoUrl} from "../assets/constants/characterVideoUrls";
import {getCharacterFromStorage, saveCharacterToStorage} from "../storage";
import {Artist, convertArtistToType} from "src/utils/artist.utils";
import {SessionAPI} from "src/services/session/logic/api/session.api";
import {SESSION_STATUS} from "src/services/session/logic/types/session.type";
import {useSessionStore} from "src/services/session/store";
import {useClearSessionMutation} from "src/services/session/logic/queries/session.queries";
import {isServiceUnavailableError} from "src/utils/error.utils";

type UseCharacterParams = {
  artist: Artist;
  onForegroundSessionNone: () => void;
  onServiceUnavailable: () => void;
};

export const useCharacter = ({
  artist,
  onForegroundSessionNone,
  onServiceUnavailable,
}: UseCharacterParams) => {
  const [selectedCharacter, setSelectedCharacter] =
    useState<CharacterInfo | null>(null);

  const clearSessionMutation = useClearSessionMutation();

  const getCharactersByArtist = (artist: Artist): CharacterInfo[] => {
    return CHARACTER_INFO[artist] ?? [];
  };

  const isCharacterInArtist = (
    character: CharacterInfo,
    artist: Artist,
  ): boolean => {
    const characters = getCharactersByArtist(artist);
    return characters.some((char) => char.id === character.id);
  };

  const handleClearSession = () => {
    // API 요청만 호출. 에러 발생 시 무시.
    clearSessionMutation.mutateAsync().catch(() => {});
  };

  const getSessionStatus = async () => {
    const type = convertArtistToType(artist);
    try {
      const {ttl} = await SessionAPI.fetchSessionStatus({query: {type}});
      useSessionStore.getState().setSessionRemainingSeconds(ttl);
    } catch (error) {
      if (isServiceUnavailableError(error)) {
        onServiceUnavailable();
      }
    }
  };

  useEffect(() => {
    getSessionStatus();
  }, []);

  useEffect(() => {
    const initializeCharacter = (): void => {
      const characters = getCharactersByArtist(artist);
      const storedCharacter = getCharacterFromStorage();
      if (storedCharacter && isCharacterInArtist(storedCharacter, artist)) {
        // 최신 데이터로 마이그레이션 처리
        const targetCharacter = characters.find(
          (char) => char.identifier === storedCharacter.identifier,
        );
        // store에 저장
        if (targetCharacter) {
          saveCharacterToStorage(targetCharacter);
          setSelectedCharacter(targetCharacter);
          return;
        }

        setSelectedCharacter(storedCharacter);
        return;
      }

      if (characters.length > 0) {
        setSelectedCharacter(characters[0]);
      }
    };

    initializeCharacter();
  }, [artist]);

  // 다음 페이지(Play) 배경 영상 preload
  useEffect(() => {
    if (!selectedCharacter) return;

    const videoUrl = getBackgroundVideoUrl(selectedCharacter.identifier);
    const video = document.createElement("video");
    video.src = videoUrl;
    video.preload = "auto";
    video.muted = true;
    video.style.display = "none";
    document.body.appendChild(video);

    return () => {
      video.src = "";
      if (document.body.contains(video)) {
        document.body.removeChild(video);
      }
    };
  }, [selectedCharacter?.identifier]);

  // 포그라운드 재진입 감지 (모바일 백그라운드 복귀 + PC 탭 전환 복귀)
  const handleVisibilityChange = async (): Promise<void> => {
    if (document.visibilityState === "hidden") {
      console.log("[useCharacter] 백그라운드 진입 감지");
      return;
    }

    console.log("[useCharacter] 포그라운드 진입 감지 → 세션 상태 확인");

    const type = convertArtistToType(artist);
    try {
      const {status, ttl} = await SessionAPI.fetchSessionStatus({
        query: {type},
      });

      if (status === SESSION_STATUS.NONE) {
        console.log("[useCharacter] 세션 상태: NONE → waiting 페이지로 이동");
        onForegroundSessionNone?.();
        return;
      }

      if (status === SESSION_STATUS.PLAY) {
        console.log("[useCharacter] 세션 상태: PLAY → 남은 시간 갱신");
        useSessionStore.getState().setSessionRemainingSeconds(ttl);
        return;
      }

      console.log(`[useCharacter] 세션 상태: ${status} (발생할 수 없는 상황)`);
    } catch (error) {
      if (isServiceUnavailableError(error)) {
        onServiceUnavailable();
        return;
      }
      console.error("[useCharacter] 세션 상태 조회 실패", error);
    }
  };

  useEffect(() => {
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [onForegroundSessionNone]);

  const changeSelectedCharacter = (character: CharacterInfo): CharacterInfo => {
    setSelectedCharacter(character);
    return character;
  };

  const saveCharacter = (): void => {
    if (selectedCharacter) {
      saveCharacterToStorage(selectedCharacter);
    }
  };

  return {
    selectedCharacter,
    getCharactersByArtist,
    changeSelectedCharacter,
    saveCharacter,
    handleClearSession,
  };
};
